import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Restyles a guide to look like the rest of the app: its colours from the app's theme, the app's font and compact
 * sizes, no page background or sticky bar of its own (the app's header and sidebar stay around it).
 */
// The app's two palettes (Bootstrap's light theme, and the dark one in index.css). Fixed here rather than read from
// the page, because a theme change re-renders this before the page's own colours switch.
const PALETTES = {
  light: { surface: '#ffffff', soft: '#f8f9fa', ink: '#212529', inkSoft: '#6c757d', faint: '#868e96', border: '#dee2e6', primary: '#0d6efd', primarySoft: '#e7f1ff' },
  dark: { surface: '#15171c', soft: '#1a1d23', ink: '#d7dae0', inkSoft: '#a3a9b3', faint: '#8a909a', border: '#262a31', primary: '#6ea8fe', primarySoft: '#0b1d3a' }
};

const inAppStyle = (theme) => {
  const dark = theme === 'dark';
  const p = dark ? PALETTES.dark : PALETTES.light;
  const font = getComputedStyle(document.body).fontFamily;
  return `<style id="in-app">
  :root, :root[data-theme="dark"], :root[data-theme="light"] {
    --bg: transparent;
    --surface: ${p.surface};
    --surface-soft: ${p.soft};
    --ink: ${p.ink};
    --ink-soft: ${p.inkSoft};
    --ink-faint: ${p.faint};
    --border: ${p.border};
    --primary: ${p.primary};
    --primary-soft: ${p.primarySoft};
    --font-display: ${font};
    --font-body: ${font};
    color-scheme: ${dark ? 'dark' : 'light'};
  }
  html, body { background: transparent !important; }
  body { margin: 0; padding-inline: 0; font-size: 13px; }
  .wrap { max-width: 920px; margin: 0; padding-block: 0 16px; }
  .jumpbar { position: static; margin-inline: 0; padding: 0 0 10px; background: transparent; }
  header.hero { padding-block: 12px 0; gap: 10px; }
  .site-link { display: none; }
  h1 { font-size: 22px; }
  .lede, .section-sub { font-size: 13.5px; }
  section { margin-top: 32px; scroll-margin-top: 16px; }
  .section-head h2 { font-size: 17px; }
  .stage { padding: 14px 16px 16px; border-radius: 8px; }
  .stage h3 { font-size: 14px; }
  .say p, .why p, .note p, .check-item p, .listen li, .clicks li, ul.plain li, details.faq .a, table.map { font-size: 13px; }
  details.faq summary { font-size: 13.5px; }
  .check-item, details.faq, .factbox, .script, .tl, table.map { border-radius: 8px; }
  footer { margin-top: 32px; font-size: 12px; }
</style>`;
};

/** In-page links scroll the app's page to the heading, since the frame itself never scrolls. */
const ANCHOR_SCRIPT = `<script>
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var target = document.getElementById(a.getAttribute('href').slice(1));
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
</script>`;

/**
 * One account manager guide, inside Account Management with the app's header and sidebar around it. The page comes
 * from the server only for account managers (and System Admins), so it is never a public file.
 */
function AccountManagerGuide({ name, onBack }) {
  const { theme } = useTheme();
  const frame = useRef(null);
  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [height, setHeight] = useState(600);

  useEffect(() => {
    let cancelled = false;
    setPage(null);
    setError(null);
    axios.get(`/api/account-manager/guides/${name}`, { responseType: 'text', transformResponse: (d) => d })
      .then((r) => { if (!cancelled) setPage(r.data); })
      .catch((e) => {
        if (!cancelled) setError(e.response?.status === 403 ? 'Only account managers can open the sales guides.' : 'Could not load this guide.');
      });
    return () => { cancelled = true; };
  }, [name]);

  const title = page ? (page.match(/<title>([^<]*)<\/title>/i) || [])[1] || 'Guide' : 'Guide';
  const srcDoc = page
    ? page.replace('</head>', `${inAppStyle(theme)}\n</head>`)
      .replace('<html lang="en">', `<html lang="en" data-theme="${theme === 'dark' ? 'dark' : 'light'}">`)
      .replace('</body>', `${ANCHOR_SCRIPT}\n</body>`)
    : null;

  // The frame grows with its page so the app's page scrolls, not the frame. Its height only changes when its width
  // does (the window or sidebar) or an answer opens or closes, so those are the events to measure on.
  const fit = useCallback(() => {
    window.requestAnimationFrame(() => {
      const doc = frame.current?.contentDocument;
      // The body, not the document: a document is never shorter than its frame, so the frame could never shrink
      const h = doc?.body ? Math.ceil(doc.body.getBoundingClientRect().height) : 0;
      if (h) setHeight((old) => (old === h ? old : h));
    });
  }, []);

  const onLoad = () => {
    fit();
    const win = frame.current?.contentWindow;
    if (win) {
      win.addEventListener('resize', fit);
      win.document.addEventListener('toggle', fit, true);
    }
  };

  const print = () => frame.current?.contentWindow?.print();

  const openInTab = () => {
    const url = URL.createObjectURL(new Blob([page], { type: 'text/html' }));
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  return (
    <div>
      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <Button size="sm" variant="link" className="px-0" onClick={onBack}>&larr; All guides</Button>
        <span className="text-muted">/</span>
        <span className="fw-semibold">{title}</span>
        <div className="ms-auto d-flex gap-2">
          <Button size="sm" variant="outline-secondary" disabled={!page} onClick={print}>
            <i className="bi bi-printer me-1" />Print
          </Button>
          <Button size="sm" variant="outline-secondary" disabled={!page} onClick={openInTab}>
            <i className="bi bi-box-arrow-up-right me-1" />Open in new tab
          </Button>
        </div>
      </div>
      {error && <Alert variant="warning">{error}</Alert>}
      {!error && !page && <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>}
      {srcDoc && (
        <iframe
          key={theme}
          ref={frame}
          title={title}
          srcDoc={srcDoc}
          onLoad={onLoad}
          style={{ width: '100%', height, border: 0, display: 'block', background: 'transparent' }}
        />
      )}
    </div>
  );
}

export default AccountManagerGuide;
